import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    TextField
} from '@mui/material';
import { ModalActionProps } from 'models/AutoModals';
import { UserModel } from 'models/UserModel';
import { CompanyType } from 'models/CompanyModel';


const defaultRoles = {
    [CompanyType.Master]: 'Manager',
    [CompanyType.Shipper]: 'Manager',
    [CompanyType.PortAgent]: 'Port Agent',
    [CompanyType.TransportAgent]: 'Transport Agent',
    [CompanyType.Party]: 'Consignee',
    [CompanyType.VesselOwner]: 'Vessel Owner',
}

type UserDialogProps = {
    user?: UserModel,
    company: UserModel['company'],
} & ModalActionProps<UserModel>;

const UserDialog = ({company, onFinish, user}: UserDialogProps) => {
    const {t} = useTranslation();
    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const data = Object.fromEntries(new FormData(event.currentTarget).entries()) as any;

        onFinish({
            ...user,
            ...data,
        });
    };

    return (
        <Box component="form" onSubmit={submit}>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="email"
                            name="email"
                            label={t('email')}
                            fullWidth
                            type="email"
                            required
                            defaultValue={user?.email}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="phone"
                            name="phone"
                            label={t('phone')}
                            fullWidth
                            type="tel"
                            defaultValue={user?.phone}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            autoFocus
                            id="lastName"
                            name="lastName"
                            label={t('last_name')}
                            fullWidth
                            required
                            defaultValue={user?.lastName}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="firstName"
                            name="firstName"
                            label={t('first_name')}
                            fullWidth
                            required
                            defaultValue={user?.firstName}
                        />
                    </Grid>
                </Grid>

                <FormControl sx={{mt: 2}}>
                    <FormLabel id="role-label">{t('role')}</FormLabel>
                    <RadioGroup
                        row
                        aria-labelledby="role-label"
                        name="role"
                        defaultValue={user?.role || defaultRoles[company.type]}
                    >
                        {(company.type === CompanyType.Master || company.type === CompanyType.Shipper) && (
                            <>
                                <FormControlLabel
                                    value="Admin"
                                    label="Admin"
                                    control={<Radio/>}
                                />

                                <FormControlLabel
                                    value="Manager"
                                    label="Manager"
                                    control={<Radio/>}
                                />

                                {company.type === CompanyType.Master && (
                                    <FormControlLabel
                                        value="F Project Manager"
                                        label="F Project Manager"
                                        control={<Radio/>}
                                    />
                                )}
                            </>
                        )}
                        {company.type === CompanyType.PortAgent && (
                            <>
                                <FormControlLabel
                                    value="Port Agent"
                                    label="Port Agent"
                                    control={<Radio/>}
                                />

                                <FormControlLabel
                                    value="Port Worker"
                                    label="Port Worker"
                                    control={<Radio/>}
                                />
                            </>
                        )}
                        {company.type === CompanyType.Party && (
                            <>
                                <FormControlLabel
                                    value="Consignee"
                                    label="Consignee"
                                    control={<Radio/>}
                                />

                                <FormControlLabel
                                    value="Notify"
                                    label="Notify"
                                    control={<Radio/>}
                                />

                                <FormControlLabel
                                    value="Dealer"
                                    label="Dealer"
                                    control={<Radio/>}
                                />
                            </>
                        )}
                        {company.type === CompanyType.TransportAgent && (
                            <>
                                <FormControlLabel
                                    value="Transport Agent"
                                    label="Transport Agent"
                                    control={<Radio/>}
                                />
                            </>
                        )}
                        {company.type === CompanyType.VesselOwner && (
                            <>
                                <FormControlLabel
                                    value="Vessel Owner"
                                    label="Vessel Owner"
                                    control={<Radio/>}
                                />

                                <FormControlLabel
                                    value="Captain"
                                    label="Captain"
                                    control={<Radio/>}
                                />
                            </>
                        )}
                    </RadioGroup>
                </FormControl>

                {!user && (
                    <FormControl sx={{mt: 2}}>
                        <FormLabel id="lang-label">{t('language')}</FormLabel>
                        <RadioGroup
                            row
                            aria-labelledby="lang-label"
                            name="lang"
                            defaultValue="en"
                        >
                            <FormControlLabel
                                value="en"
                                label="English"
                                control={<Radio/>}
                            />

                            <FormControlLabel
                                value="ru"
                                label="Русский"
                                control={<Radio/>}
                            />

                            <FormControlLabel
                                value="ja"
                                label="日本語"
                                control={<Radio/>}
                            />
                        </RadioGroup>
                    </FormControl>
                )}
            </DialogContent>

            <DialogActions>
                <Button type="submit">{user ? t('save') : t('add')}</Button>
            </DialogActions>
        </Box>
    );
};

export default UserDialog;
